import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  BugReport as DetectionIcon,
  FolderOpen as CaseIcon,
  AutoAwesome as AIIcon,
  Visibility as SightingIcon,
  Shield as ThreatIntelIcon,
  Refresh as RefreshIcon,
  CloudUpload as PushIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePermission } from '../hooks/usePermission';
import { formatDateTime } from '../utils/dateUtils';
import { IndicatorDialog, getIndicatorErrorMessage } from '../components/Indicators/IndicatorDialog';
import PushToEdrDialog from '../components/Indicators/PushToEdrDialog';
import IntelResultList from '../components/Intel/IntelResultList';
import { verdictMeta } from '../components/Intel/verdict';
import { capabilitiesForType } from '../components/Integrations/agentMeta';

const confidenceColors = {
  Low: '#757575',
  Medium: '#ff9800',
  High: '#4caf50',
};

const tlpColors = {
  WHITE: '#ffffff',
  GREEN: '#4caf50',
  AMBER: '#ff9800',
  RED: '#d32f2f',
};

const statusChipColor = {
  Active: 'success',
  Inactive: 'default',
  Expired: 'warning',
  'False Positive': 'error',
};

const personName = (p) => {
  if (!p) return '';
  if (typeof p === 'string') return p;
  const full = [p.firstName, p.lastName].filter(Boolean).join(' ');
  return full || p.username || p.email || '';
};

const humanize = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

const DetailRow = ({ label, children, mono = false }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined} sx={{ wordBreak: 'break-all' }}>
      {children === undefined || children === null || children === '' ? '-' : children}
    </Typography>
  </Grid>
);

/** Renders whatever shape aiAnalysis happens to be (string, array or object). */
const AIAnalysisBlock = ({ analysis }) => {
  if (!analysis) return null;
  if (typeof analysis === 'string') {
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{analysis}</Typography>;
  }
  if (Array.isArray(analysis)) {
    return (
      <List dense disablePadding>
        {analysis.map((item, i) => (
          <ListItem key={i} disableGutters>
            <ListItemText primary={typeof item === 'string' ? item : JSON.stringify(item)} />
          </ListItem>
        ))}
      </List>
    );
  }
  return (
    <Grid container spacing={2}>
      {Object.entries(analysis).map(([key, value]) => {
        if (value === null || value === undefined || value === '') return null;
        let body;
        if (Array.isArray(value)) {
          body = (
            <List dense disablePadding>
              {value.map((item, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0 }}>
                  <ListItemText primary={typeof item === 'string' ? item : JSON.stringify(item)} />
                </ListItem>
              ))}
            </List>
          );
        } else if (typeof value === 'object') {
          body = (
            <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(value, null, 2)}
            </Box>
          );
        } else {
          body = <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{String(value)}</Typography>;
        }
        return (
          <Grid item xs={12} key={key}>
            <Typography variant="subtitle2">{humanize(key)}</Typography>
            {body}
          </Grid>
        );
      })}
    </Grid>
  );
};

const IndicatorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getSeverityColor } = useSettings();
  const { hasPermission } = usePermission();

  const canUpdate = hasPermission('indicators', 'update');
  const canDelete = hasPermission('indicators', 'delete');

  const [indicator, setIndicator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [enrichingIntel, setEnrichingIntel] = useState(false);

  // EDR push
  const [agents, setAgents] = useState([]);
  const [agentTypes, setAgentTypes] = useState([]);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);

  // Add-sighting form
  const [sightingForm, setSightingForm] = useState({ source: '', context: '' });
  const [addingSighting, setAddingSighting] = useState(false);
  const [sightingError, setSightingError] = useState('');

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const fmt = (d) => (d ? formatDateTime(d, user?.preferences) : '-');

  const fetchIndicator = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.indicators.getById(id);
      setIndicator(response.data.indicator);
    } catch (err) {
      console.error('Failed to fetch indicator:', err);
      setError(err.response?.status === 404 ? 'Indicator not found' : 'Failed to load indicator');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchIndicator();
  }, [fetchIndicator]);

  // EDR-capable agents, loaded once to size the "Push to EDR" affordance; failures just
  // leave the button disabled rather than blocking the page.
  useEffect(() => {
    Promise.all([apiService.agents.getAll(), apiService.agents.getTypes()])
      .then(([agentsRes, typesRes]) => {
        setAgents(agentsRes.data.agents || []);
        setAgentTypes(typesRes.data.types || []);
      })
      .catch((err) => console.error('Failed to load EDR agents:', err));
  }, []);

  const handleAddSighting = async () => {
    if (!sightingForm.source.trim()) {
      setSightingError('Source is required');
      return;
    }
    try {
      setAddingSighting(true);
      setSightingError('');
      await apiService.indicators.addSighting(id, {
        source: sightingForm.source.trim(),
        context: sightingForm.context.trim() || undefined,
      });
      setSightingForm({ source: '', context: '' });
      notify('Sighting added');
      fetchIndicator();
    } catch (err) {
      console.error('Failed to add sighting:', err);
      setSightingError(getIndicatorErrorMessage(err, 'Failed to add sighting'));
    } finally {
      setAddingSighting(false);
    }
  };

  const handleEnrichIndicator = async (refresh = false) => {
    try {
      setEnrichingIntel(true);
      const res = await apiService.enrichment.enrichIndicator(id, refresh);
      const updatedIndicator = res.data?.indicator;
      if (updatedIndicator) setIndicator(updatedIndicator);
      notify('Threat intelligence updated');
    } catch (err) {
      console.error('Failed to enrich indicator:', err);
      notify(getIndicatorErrorMessage(err, 'Failed to enrich indicator with threat intelligence'), 'error');
    } finally {
      setEnrichingIntel(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiService.indicators.delete(id);
      navigate('/dashboard/indicators');
    } catch (err) {
      console.error('Failed to delete indicator:', err);
      notify(getIndicatorErrorMessage(err, 'Failed to delete indicator'), 'error');
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !indicator) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/indicators')} sx={{ mb: 2 }}>
          Back to Indicators
        </Button>
        <Alert severity="error">{error || 'Indicator not found'}</Alert>
      </Box>
    );
  }

  const sightings = [...(indicator.sightings || [])].sort(
    (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
  );
  const detections = indicator.detections || [];
  const cases = indicator.cases || [];
  const relatedIndicators = (indicator.relatedIndicators || []).filter((r) => r?.indicator);
  const tlp = indicator.tlp || 'WHITE';
  const intel = indicator.intel;
  const intelVerdictMeta = verdictMeta(intel?.overall?.verdict);
  const deployments = [...(indicator.deployments || [])].sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  const edrEligibleCount = agents.filter((a) => a.isEnabled && capabilitiesForType(a.type, agentTypes).iocManagement).length;

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" component="button" onClick={() => navigate('/dashboard')}>
          Dashboard
        </Link>
        <Link underline="hover" color="inherit" component="button" onClick={() => navigate('/dashboard/indicators')}>
          Indicators
        </Link>
        <Typography color="text.primary" fontFamily="monospace">
          {indicator.value?.length > 40 ? `${indicator.value.slice(0, 40)}...` : indicator.value}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            {indicator.type}
          </Typography>
          <Typography variant="h5" fontFamily="monospace" sx={{ wordBreak: 'break-all' }} gutterBottom>
            {indicator.value}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label={`TLP:${tlp}`}
              size="small"
              sx={{
                backgroundColor: tlpColors[tlp] || tlpColors.WHITE,
                color: tlp === 'WHITE' ? 'black' : 'white',
                border: tlp === 'WHITE' ? '1px solid #ccc' : 'none',
              }}
            />
            <Chip
              label={`Confidence: ${indicator.confidence || 'Unknown'}`}
              size="small"
              sx={{ backgroundColor: confidenceColors[indicator.confidence] || '#757575', color: 'white' }}
            />
            {indicator.severity && (
              <Chip
                label={`Severity: ${indicator.severity}`}
                size="small"
                sx={{ backgroundColor: getSeverityColor(indicator.severity).main, color: 'white' }}
              />
            )}
            <Chip
              label={indicator.status || 'Unknown'}
              size="small"
              variant="outlined"
              color={statusChipColor[indicator.status] || 'default'}
            />
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/indicators')}>
            Back
          </Button>
          {canUpdate && (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Details</Typography>
              <Grid container spacing={2}>
                <DetailRow label="Type">{indicator.type}</DetailRow>
                <DetailRow label="Source">{indicator.source}</DetailRow>
                <DetailRow label="Value" mono>{indicator.value}</DetailRow>
                <DetailRow label="Status">{indicator.status}</DetailRow>
                <DetailRow label="First Seen">{fmt(indicator.firstSeen)}</DetailRow>
                <DetailRow label="Last Seen">{fmt(indicator.lastSeen)}</DetailRow>
                <DetailRow label="Created">
                  {fmt(indicator.createdAt)}{personName(indicator.createdBy) ? ` by ${personName(indicator.createdBy)}` : ''}
                </DetailRow>
                <DetailRow label="Last Modified">
                  {fmt(indicator.updatedAt)}{personName(indicator.lastModifiedBy) ? ` by ${personName(indicator.lastModifiedBy)}` : ''}
                </DetailRow>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {indicator.description || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Tags
                  </Typography>
                  {indicator.tags?.length ? (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {indicator.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <SightingIcon color="action" />
                <Typography variant="h6">Sightings ({sightings.length})</Typography>
              </Box>

              {canUpdate && (
                <Box mb={2}>
                  {sightingError && <Alert severity="error" sx={{ mb: 2 }}>{sightingError}</Alert>}
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Source"
                        value={sightingForm.source}
                        onChange={(e) => setSightingForm((f) => ({ ...f, source: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Context"
                        value={sightingForm.context}
                        onChange={(e) => setSightingForm((f) => ({ ...f, context: e.target.value }))}
                        inputProps={{ maxLength: 500 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddSighting}
                        disabled={addingSighting}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              )}

              {sightings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No sightings recorded yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Context</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sightings.map((s, i) => (
                      <TableRow key={s._id || i}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(s.timestamp)}</TableCell>
                        <TableCell>{s.source}</TableCell>
                        <TableCell>{s.context || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                <Box>
                  <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                    <ThreatIntelIcon color="primary" /> Threat Intelligence
                    {intel?.overall?.verdict && (
                      <Chip size="small" color={intelVerdictMeta.color} label={intelVerdictMeta.label} sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  {intel?.enrichedAt && (
                    <Tooltip title={fmt(intel.enrichedAt)}>
                      <Typography variant="caption" color="text.secondary">
                        Checked {formatDistanceToNow(new Date(intel.enrichedAt), { addSuffix: true })}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
                {canUpdate && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={enrichingIntel ? <CircularProgress size={14} /> : (intel ? <RefreshIcon /> : <ThreatIntelIcon />)}
                    onClick={() => handleEnrichIndicator(!!intel)}
                    disabled={enrichingIntel}
                  >
                    {enrichingIntel ? 'Checking...' : intel ? 'Refresh' : 'Enrich now'}
                  </Button>
                )}
              </Box>

              {intel?.skipped && (
                <Alert severity="info" sx={{ mb: 2 }}>{intel.skipped}</Alert>
              )}

              {!intel && (
                <Typography variant="body2" color="text.secondary">
                  Enrichment queries the threat-intel providers configured under{' '}
                  <Link component="button" underline="hover" onClick={() => navigate('/dashboard/integrations')}>Integrations</Link> for this indicator.
                </Typography>
              )}

              {intel && !intel.skipped && (
                <IntelResultList lookup={{ ...intel, value: indicator.value }} />
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  <PushIcon color="primary" /> EDR Deployment
                </Typography>
                {canUpdate && (
                  <Tooltip title={edrEligibleCount === 0 ? 'No enabled integration supports indicator management (CrowdStrike, SentinelOne, Microsoft Defender)' : ''}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PushIcon />}
                        onClick={() => setPushDialogOpen(true)}
                        disabled={edrEligibleCount === 0}
                      >
                        Push to EDR
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>

              {deployments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Not yet deployed to any EDR integration.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {deployments.map((d, i) => (
                    <ListItem key={`${d.agentId}-${i}`} disableGutters alignItems="flex-start">
                      <ListItemText
                        primary={(
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {d.vendor} · {d.agentName}
                            </Typography>
                            <Chip size="small" variant="outlined" label={d.action === 'block' ? 'Block' : 'Detect'} />
                            <Chip
                              size="small"
                              label={d.status === 'deployed' ? 'Deployed' : 'Failed'}
                              color={d.status === 'deployed' ? 'success' : 'error'}
                            />
                          </Box>
                        )}
                        secondary={[d.at ? fmt(d.at) : null, d.error].filter(Boolean).join(' · ')}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {indicator.aiAnalysis && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <AIIcon color="primary" />
                  <Typography variant="h6">AI Analysis</Typography>
                </Box>
                <AIAnalysisBlock analysis={indicator.aiAnalysis} />
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DetectionIcon color="action" />
                <Typography variant="h6">Related Detections ({detections.length})</Typography>
              </Box>
              {detections.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No linked detections.</Typography>
              ) : (
                <List dense disablePadding>
                  {detections.map((d) => (
                    <ListItem key={d._id} disablePadding>
                      <ListItemButton onClick={() => navigate(`/dashboard/detections/${d._id}`)}>
                        <ListItemText
                          primary={d.title || d._id}
                          secondary={[d.severity, d.status].filter(Boolean).join(' / ')}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CaseIcon color="action" />
                <Typography variant="h6">Related Cases ({cases.length})</Typography>
              </Box>
              {cases.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No linked cases.</Typography>
              ) : (
                <List dense disablePadding>
                  {cases.map((c) => (
                    <ListItem key={c._id} disablePadding>
                      <ListItemButton onClick={() => navigate(`/dashboard/cases/${c._id}`)}>
                        <ListItemText
                          primary={c.caseNumber ? `${c.caseNumber} - ${c.title || ''}` : c.title || c._id}
                          secondary={c.status}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {relatedIndicators.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Related Indicators</Typography>
                <List dense disablePadding>
                  {relatedIndicators.map((r, i) => (
                    <ListItem key={r.indicator._id || i} disablePadding>
                      <ListItemButton onClick={() => navigate(`/dashboard/indicators/${r.indicator._id}`)}>
                        <ListItemText
                          primary={<span style={{ fontFamily: 'monospace' }}>{r.indicator.value}</span>}
                          secondary={[r.indicator.type, r.relationship].filter(Boolean).join(' / ')}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <IndicatorDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        indicator={indicator}
        onSaved={() => {
          notify('Indicator updated');
          fetchIndicator();
        }}
      />

      <PushToEdrDialog
        open={pushDialogOpen}
        onClose={() => setPushDialogOpen(false)}
        indicatorId={id}
        onPushed={(data) => {
          if (data.indicator) setIndicator(data.indicator);
          const agentsResult = data.agents || [];
          const succeeded = agentsResult.filter((a) => a.success).length;
          notify(
            succeeded === agentsResult.length && agentsResult.length > 0
              ? `Pushed to ${succeeded} agent(s)`
              : `Pushed to ${succeeded}/${agentsResult.length} agent(s)`,
            succeeded === agentsResult.length && agentsResult.length > 0 ? 'success' : 'warning'
          );
        }}
      />

      <Dialog open={deleteOpen} onClose={deleting ? undefined : () => setDeleteOpen(false)}>
        <DialogTitle>Delete Indicator</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the {indicator.type} indicator{' '}
            <Box component="span" fontFamily="monospace">{indicator.value}</Box>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IndicatorDetailPage;
